import { Check, Gamepad2 } from "lucide-react";
import { categoryExamples } from "@/content/categories";
import { useCategoryStore } from "@/store/categoryStore";
import { useCategories } from "@/store/customContentStore";

/** Grouped, toggleable list of design categories. Selection is persisted. */
export default function CategoryPicker({ compact = false }: { compact?: boolean }) {
  const { active, toggle, setAll, reset } = useCategoryStore();
  const CATEGORIES = useCategories();

  const groups = Array.from(new Set(CATEGORIES.map((c) => c.group)));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted">
          {active.length} of {CATEGORIES.length} active
        </span>
        <div className="flex gap-2 text-xs">
          <button onClick={() => setAll(true)} className="rounded-md px-2 py-1 text-muted hover:text-text">
            All
          </button>
          <button onClick={() => setAll(false)} className="rounded-md px-2 py-1 text-muted hover:text-text">
            None
          </button>
          <button onClick={reset} className="rounded-md px-2 py-1 text-muted hover:text-text">
            Reset
          </button>
        </div>
      </div>

      {groups.map((group) => (
        <div key={group}>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
            {group}
          </div>
          <div className={compact ? "flex flex-wrap gap-2" : "grid gap-2 sm:grid-cols-2"}>
            {CATEGORIES.filter((c) => c.group === group).map((c) => {
              const on = active.includes(c.id);
              const examples = categoryExamples(c.id);
              return (
                <div key={c.id} className="group relative">
                  <button
                    onClick={() => toggle(c.id)}
                    className={[
                      "flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                      on
                        ? "border-primary/60 bg-primary/10 text-text"
                        : "border-border bg-surface-2 text-muted hover:text-text",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "grid h-4 w-4 shrink-0 place-items-center rounded border",
                        on ? "border-primary bg-primary text-primary-fg" : "border-border",
                      ].join(" ")}
                    >
                      {on && <Check className="h-3 w-3" />}
                    </span>
                    <span className="truncate">{c.name}</span>
                  </button>

                  {/* Hover tooltip: description + real game examples */}
                  {(c.description || examples.length > 0) && (
                    <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 hidden w-64 -translate-x-1/2 rounded-xl border border-border bg-surface p-3 text-left shadow-2xl group-hover:block">
                      <div className="font-display text-sm font-semibold text-text">{c.name}</div>
                      {c.description && <p className="mt-0.5 text-xs text-muted">{c.description}</p>}
                      {examples.length > 0 && (
                        <div className="mt-2 border-t border-border pt-2">
                          <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
                            <Gamepad2 className="h-3 w-3" /> Real examples
                          </div>
                          <div className="text-xs text-text/90">{examples.join(" · ")}</div>
                        </div>
                      )}
                      <div className="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1 rotate-45 border-b border-r border-border bg-surface" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
